import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavigationHeader from "../../components/ui/NavigationHeader";
import Input from "../../components/ui/Input";
import Select from "../../components/ui/Select";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";

import { useAuth } from "../../contexts/AuthContext";
import { useCurrency } from "../../contexts/CurrencyContext";

import { defaultPortfolioConfig } from "../../lib/portfolio/config";
import { pctToDecimal, num, requireFields } from "../../lib/portfolio/validators";
import { projectPortfolioAnnual } from "../../lib/portfolio/projection";

import ComparateurTermeHypothecaire from "../../components/portfolio/ComparateurTermeHypothecaire";
import { listPortfolioProperties, createPortfolioProperty, deletePortfolioProperty } from "../../services/portfolioService";

import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend
} from "recharts";

const horizons = [
  { value: 10, label: "10 ans" },
  { value: 20, label: "20 ans" },
  { value: 30, label: "30 ans" }
];

const objectives = [
  { value: "RECYCLE_RAPIDE", label: "Recycle rapide (cash-out tôt)" },
  { value: "STABILITE", label: "Stabilité (cashflow + risque)" }
];

export default function PortfolioProjectionPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [cfg, setCfg] = useState(defaultPortfolioConfig);
  const [horizonYears, setHorizonYears] = useState(30);
  const [objective, setObjective] = useState("RECYCLE_RAPIDE");

  const [properties, setProperties] = useState([]);
  const [selectedId, setSelectedId] = useState(null);

  // Form (achat en cours)
  const [form, setForm] = useState({
    label: "",
    acquisitionDate: "",
    purchasePrice: "",
    downPayment: "",
    mortgageAmount: "",
    amortizationYears: "25",
    rate3: "",
    rate5: "",
    rate7: "",
    rate10: "",

    // Baseline (MVP)
    grossIncomeAnnual: "",
    operatingExpensesAnnual: "",

    // Assumptions
    appreciationAnnual: "3",
    rentGrowthAnnual: "2",
    expenseInflationAnnual: "2"
  });

  const selected = useMemo(() => properties.find((p) => p.id === selectedId) || null, [properties, selectedId]);

  useEffect(() => {
    if (!user?.id) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const rows = await listPortfolioProperties();
        setProperties(rows);
        setSelectedId(rows?.[0]?.id || null);
      } catch (e) {
        console.error(e);
        setError(e?.message || "Impossible de charger vos propriétés.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user?.id]);

  const normalizedProperties = useMemo(() => {
    return (properties || []).map((p) => ({
      id: p.id,
      inputs: p.inputs_json || {},
      assumptions: p.assumptions_json || {}
    }));
  }, [properties]);

  const projection = useMemo(() => {
    if (!normalizedProperties?.length) return null;
    try {
      return projectPortfolioAnnual({
        properties: normalizedProperties,
        cfg,
        horizonYears: Number(horizonYears),
        objective
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [normalizedProperties, cfg, horizonYears, objective]);

  const chartData = projection?.series || [];

  const handleFormChange = (k, v) => {
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const handleCfgChange = (path, v) => {
    // path: 'conservative.dscrMin'
    setCfg((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const [a, b] = path.split(".");
      next[a][b] = v;
      return next;
    });
  };

  const resetForm = () => {
    setForm((prev) => ({
      ...prev,
      label: "",
      acquisitionDate: "",
      purchasePrice: "",
      downPayment: "",
      mortgageAmount: "",
      amortizationYears: "25",
      rate3: "",
      rate5: "",
      rate7: "",
      rate10: "",
      grossIncomeAnnual: "",
      operatingExpensesAnnual: ""
    }));
  };

  const handleAddProperty = async () => {
    setError(null);

    const required = requireFields(form, ["acquisitionDate", "purchasePrice", "mortgageAmount", "amortizationYears"]);
    if (!required.ok) {
      setError(`Champs manquants: ${required.missing.join(", ")}`);
      return;
    }

    const inputs_json = {
      label: form.label,
      acquisitionDate: form.acquisitionDate,
      purchasePrice: num(form.purchasePrice, 0),
      downPayment: num(form.downPayment, 0),
      mortgageAmount: num(form.mortgageAmount, 0),
      amortizationYears: num(form.amortizationYears, 25),
      paymentFrequency: "monthly",
      ratesByTerm: {
        3: num(form.rate3, 0),
        5: num(form.rate5, 0),
        7: num(form.rate7, 0),
        10: num(form.rate10, 0)
      },
      // MVP baseline
      grossIncomeAnnual: num(form.grossIncomeAnnual, 0),
      operatingExpensesAnnual: num(form.operatingExpensesAnnual, 0)
    };

    const assumptions_json = {
      appreciationAnnual: pctToDecimal(form.appreciationAnnual, 0.03),
      rentGrowthAnnual: pctToDecimal(form.rentGrowthAnnual, 0.02),
      expenseInflationAnnual: pctToDecimal(form.expenseInflationAnnual, 0.02)
    };

    const payload = {
      user_id: user.id,
      acquisition_date: form.acquisitionDate,
      inputs_json,
      assumptions_json
    };

    setLoading(true);
    try {
      const created = await createPortfolioProperty(payload);
      const next = [created, ...(properties || [])];
      setProperties(next);
      setSelectedId(created.id);
      resetForm();
    } catch (e) {
      console.error(e);
      setError(e?.message || "Impossible d'ajouter la propriété.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProperty = async (id) => {
    setLoading(true);
    try {
      await deletePortfolioProperty(id);
      const next = (properties || []).filter((p) => p.id !== id);
      setProperties(next);
      setSelectedId(next?.[0]?.id || null);
    } catch (e) {
      console.error(e);
      setError(e?.message || "Impossible de supprimer.");
    } finally {
      setLoading(false);
    }
  };

  if (!user?.id) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationHeader />
        <div className="max-w-5xl mx-auto p-6">
          <div className="rounded-2xl border border-border bg-card p-6">
            <div className="text-lg font-semibold">Connexion requise</div>
            <div className="text-muted-foreground mt-1">Connecte-toi pour accéder à la projection de portefeuille.</div>
            <div className="mt-4">
              <Button onClick={() => navigate("/login")}>Se connecter</Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationHeader />

      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-2xl font-semibold">Projection de portefeuille</div>
            <div className="text-sm text-muted-foreground">Module conservateur (10–30 ans) + comparateur de terme (3/5/7/10).</div>
          </div>
          <div className="flex gap-2">
            <Select
              value={horizonYears}
              onChange={(v) => setHorizonYears(Number(v))}
              options={horizons}
              className="min-w-[170px]"
            />
            <Select
              value={objective}
              onChange={(v) => setObjective(v)}
              options={objectives}
              className="min-w-[260px]"
            />
          </div>
        </div>

        {error && (
          <div className="rounded-xl border border-destructive/40 bg-destructive/10 text-destructive px-4 py-3 text-sm">
            {error}
          </div>
        )}

        {/* Ajouter une propriété */}
        <div className="rounded-2xl border border-border bg-card p-4 md:p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Icon name="PlusCircle" className="text-muted-foreground" size={20} />
            <div className="text-lg font-semibold">Ajouter une propriété (achat en cours)</div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Nom (optionnel)" value={form.label} onChange={(e) => handleFormChange("label", e.target.value)} placeholder="Ex: Duplex Victoriaville" />
            <Input label="Date d'acquisition" type="date" required value={form.acquisitionDate} onChange={(e) => handleFormChange("acquisitionDate", e.target.value)} />
            <Input label="Amortissement (années)" required value={form.amortizationYears} onChange={(e) => handleFormChange("amortizationYears", e.target.value)} />

            <Input label="Prix d'achat" required value={form.purchasePrice} onChange={(e) => handleFormChange("purchasePrice", e.target.value)} />
            <Input label="Mise de fonds" value={form.downPayment} onChange={(e) => handleFormChange("downPayment", e.target.value)} />
            <Input label="Montant du prêt" required value={form.mortgageAmount} onChange={(e) => handleFormChange("mortgageAmount", e.target.value)} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Input label="Taux 3 ans (%)" value={form.rate3} onChange={(e) => handleFormChange("rate3", e.target.value)} />
            <Input label="Taux 5 ans (%)" value={form.rate5} onChange={(e) => handleFormChange("rate5", e.target.value)} />
            <Input label="Taux 7 ans (%)" value={form.rate7} onChange={(e) => handleFormChange("rate7", e.target.value)} />
            <Input label="Taux 10 ans (%)" value={form.rate10} onChange={(e) => handleFormChange("rate10", e.target.value)} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Revenus bruts annuels (MVP)"
              value={form.grossIncomeAnnual}
              onChange={(e) => handleFormChange("grossIncomeAnnual", e.target.value)}
              tooltip="MVP: si ton moteur actuel calcule déjà les revenus, tu pourras enlever ce champ et brancher getBaseSnapshot() sur tes calculs existants."
            />
            <Input
              label="Dépenses d'exploitation annuelles (MVP)"
              value={form.operatingExpensesAnnual}
              onChange={(e) => handleFormChange("operatingExpensesAnnual", e.target.value)}
              tooltip="Sans dette (pas d'hypothèque ici). Inclut taxes, assurances, entretien, etc."
            />
          </div>

          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="text-sm font-semibold">Hypothèses de projection</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input label="Appréciation annuelle (%)" value={form.appreciationAnnual} onChange={(e) => handleFormChange("appreciationAnnual", e.target.value)} />
              <Input label="Croissance des loyers (%)" value={form.rentGrowthAnnual} onChange={(e) => handleFormChange("rentGrowthAnnual", e.target.value)} />
              <Input label="Inflation des dépenses (%)" value={form.expenseInflationAnnual} onChange={(e) => handleFormChange("expenseInflationAnnual", e.target.value)} />
            </div>
          </div>

          <div className="rounded-xl border border-border p-4 space-y-3">
            <div className="text-sm font-semibold">Paramètres conservateurs</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Input label="LTV max (%)" value={(cfg.conservative.ltvMaxRefi * 100).toFixed(0)} onChange={(e) => handleCfgChange("conservative.ltvMaxRefi", pctToDecimal(e.target.value, 0.8))} />
              <Input label="DSCR min" value={cfg.conservative.dscrMin} onChange={(e) => handleCfgChange("conservative.dscrMin", num(e.target.value, 1.2))} />
              <Input label="Vacance (%)" value={(cfg.conservative.vacancyRate * 100).toFixed(0)} onChange={(e) => handleCfgChange("conservative.vacancyRate", pctToDecimal(e.target.value, 0.05))} />
              <Input label="CapEx (%)" value={(cfg.conservative.capexReserveRate * 100).toFixed(0)} onChange={(e) => handleCfgChange("conservative.capexReserveRate", pctToDecimal(e.target.value, 0.05))} />
              <Input label="Stress floor (%)" value={cfg.conservative.stressTestFloorRate} onChange={(e) => handleCfgChange("conservative.stressTestFloorRate", num(e.target.value, 5.25))} />
            </div>
            <div className="text-xs text-muted-foreground">Stress test = max(taux contrat + {cfg.conservative.stressTestBuffer}%, {cfg.conservative.stressTestFloorRate}%).</div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleAddProperty} disabled={loading}>
              {loading ? "En cours..." : "Ajouter"}
            </Button>
            <Button variant="outline" onClick={resetForm} disabled={loading}>
              Réinitialiser
            </Button>
          </div>
        </div>

        {/* Liste + comparateur */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="rounded-2xl border border-border bg-card p-4 space-y-3">
            <div className="text-lg font-semibold">Propriétés (achat en cours)</div>
            {loading && !properties?.length ? (
              <div className="text-sm text-muted-foreground">Chargement...</div>
            ) : properties?.length ? (
              <div className="space-y-2">
                {properties.map((p) => (
                  <button
                    key={p.id}
                    className={`w-full text-left rounded-xl border p-3 transition ${selectedId === p.id ? "border-primary bg-primary/10" : "border-border hover:bg-muted/40"}`}
                    onClick={() => setSelectedId(p.id)}
                  >
                    <div className="font-medium">{p.inputs_json?.label || "Propriété"}</div>
                    <div className="text-xs text-muted-foreground">Acquisition: {p.acquisition_date}</div>
                    <div className="text-xs text-muted-foreground">Prix: {formatCurrency(p.inputs_json?.purchasePrice || 0)} • Prêt: {formatCurrency(p.inputs_json?.mortgageAmount || 0)}</div>
                    <div className="mt-2 flex justify-between items-center">
                      <span className="text-xs text-muted-foreground">ID: {String(p.id).slice(0, 8)}…</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteProperty(p.id);
                        }}
                      >
                        Supprimer
                      </Button>
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Aucune propriété ajoutée.</div>
            )}
          </div>

          <div className="lg:col-span-2">
            {selected ? (
              <ComparateurTermeHypothecaire
                inputs={selected.inputs_json}
                assumptions={selected.assumptions_json}
                cfg={cfg}
                objective={objective}
              />
            ) : (
              <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
                Sélectionne une propriété pour voir le comparateur.
              </div>
            )}
          </div>
        </div>

        {/* Projection portefeuille */}
        <div className="rounded-2xl border border-border bg-card p-4 md:p-6 space-y-4">
          <div className="flex items-center gap-2">
            <Icon name="LineChart" className="text-muted-foreground" size={20} />
            <div className="text-lg font-semibold">Projection portefeuille (annuelle)</div>
          </div>

          {!projection ? (
            <div className="text-sm text-muted-foreground">Ajoute au moins une propriété pour afficher la projection.</div>
          ) : (
            <>
              <div className="h-[320px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <XAxis dataKey="yearIndex" />
                    <YAxis />
                    <Tooltip
                      formatter={(v, name) => {
                        if (name === "dscrGlobal") return [v, "DSCR global"];
                        return [formatCurrency(v), name];
                      }}
                      labelFormatter={(l) => `Année +${l}`}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="valueTotal" name="Valeur" dot={false} />
                    <Line type="monotone" dataKey="debtTotal" name="Dette" dot={false} />
                    <Line type="monotone" dataKey="equityTotal" name="Équité" dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="rounded-xl border border-border p-4">
                  <div className="text-xs text-muted-foreground">Valeur totale (fin horizon)</div>
                  <div className="text-lg font-semibold">{formatCurrency(chartData?.[chartData.length - 1]?.valueTotal || 0)}</div>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="text-xs text-muted-foreground">Dette totale (fin horizon)</div>
                  <div className="text-lg font-semibold">{formatCurrency(chartData?.[chartData.length - 1]?.debtTotal || 0)}</div>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="text-xs text-muted-foreground">Équité totale (fin horizon)</div>
                  <div className="text-lg font-semibold">{formatCurrency(chartData?.[chartData.length - 1]?.equityTotal || 0)}</div>
                </div>
                <div className="rounded-xl border border-border p-4">
                  <div className="text-xs text-muted-foreground">Cashflow annuel (fin horizon)</div>
                  <div className="text-lg font-semibold">{formatCurrency(chartData?.[chartData.length - 1]?.cashflowTotal || 0)}</div>
                </div>
              </div>

              <div className="rounded-xl border border-border p-4 space-y-3">
                <div className="text-sm font-semibold">Opportunités à venir</div>
                <div className="overflow-x-auto">
                  <table className="min-w-[860px] w-full text-sm">
                    <thead>
                      <tr className="text-left text-muted-foreground">
                        <th className="py-2">Propriété</th>
                        <th>Prochain renouvellement</th>
                        <th>Terme suggéré</th>
                        <th>Cash-out estimé</th>
                        <th>Statut</th>
                        <th>Pourquoi</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {projection.opportunities.map((o) => (
                        <tr key={o.propertyId}>
                          <td className="py-2 font-medium">{o.label}</td>
                          <td>{o.renewalDateISO || "—"}</td>
                          <td>{o.bestTerm ? `${o.bestTerm} ans` : "—"}</td>
                          <td>{formatCurrency(o.cashOutMax || 0)}</td>
                          <td>{o.status || "—"}</td>
                          <td className="text-muted-foreground">{o.reasons?.length ? o.reasons.join(" • ") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="text-xs text-muted-foreground">
                  Note MVP: la projection n'applique pas encore d'événements (projets) ni de refinancement automatique; elle sert à visualiser le portefeuille et les fenêtres de renouvellement.
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
